import { createApi } from '@reduxjs/toolkit/query/react'
import {
  BuyingPower,
  EmployeeCeremonyResponse,
  EmployeeInfo,
  EmployeeIntegrityCheckResult,
  EmployeePublicInfo,
  IEmployeeInfo,
} from '@/features/employee-data/employee-data.def.ts'
import {
  AddAnnualPerformanceRequest,
  AddBackgroundCheckRequest,
  AddBankAccountRequest,
  AddBonusRequest,
  AddContractEmploymentRequest,
  AddCostDistributionRequest,
  AddDeferredProgramRequest,
  AddDependentInfoRequest,
  AddEducationRequest,
  AddEmergencyContactRequest,
  AddEmployeeRequest,
  AddEmploymentHistoryRequest,
  AddEmploymentRequest,
  AddExceptionalAwardRequest,
  AddFinanceManagedDataRequest,
  AddGroupEmploymentHistoryRequest,
  AddIdentificationDocumentRequest,
  AddLifeInsuranceRequest,
  AddMedicalInsuranceRequest,
  AddPerformanceImprovementPlanRequest,
  AddPersonalInfoRequest,
  AddPmAnnualPerformanceRequest,
  AddPmInfoRequest,
  AddPmStaticInfoRequest,
  AddQualificationRequest,
  AddSalaryRequest,
  AddStaffCostRequest,
  AddVisaRequest,
  AddWarningLetterRequest,
  AddWorkInfoRequest,
  BulkDeleteStaffCostRequest,
  BulkImportStaffCostRequest,
  BulkUpdateStaffCostRequest,
  DeleteEmployeeDataRequest,
  DeleteEmployeeRequest,
  DownloadEmployeeDataChangeLogsRequest,
  DownloadEmployeeDataRequest,
  DownloadEmployeeDocumentRequest,
  DownloadEmployeeSnapshotDataRequest,
  DownloadFundStaffCostReportRequest,
  DownloadHeadcountPassthroughReportRequest,
  DownloadSalaryHistoryReportRequest,
  EditAnnualPerformanceRequest,
  EditBackgroundCheckRequest,
  EditBankAccountRequest,
  EditBonusRequest,
  EditContractEmploymentRequest,
  EditCostDistributionRequest,
  EditDeferredProgramRequest,
  EditDependentInfoRequest,
  EditEducationRequest,
  EditEmergencyContactRequest,
  EditEmployeeRequest,
  EditEmploymentHistoryRequest,
  EditEmploymentRequest,
  EditExceptionalAwardRequest,
  EditFinanceManagedDataRequest,
  EditGroupEmploymentHistoryRequest,
  EditIdentificationDocumentRequest,
  EditLifeInsuranceRequest,
  EditMedicalInsuranceRequest,
  EditPerformanceImprovementPlanRequest,
  EditPersonalInfoRequest,
  EditPmAnnualPerformanceRequest,
  EditPmInfoRequest,
  EditPmStaticInfoRequest,
  EditQualificationRequest,
  EditSalaryRequest,
  EditStaffCostRequest,
  EditVisaRequest,
  EditWarningLetterRequest,
  EditWorkInfoRequest,
  ExportStaffCostDataRequest,
  GetEmployeeBuyingPowerRequest,
  GetEmployeeDetailsWithTimelineRequest,
  GetEmployeeDirectoryRequest,
  GetEmployeeEventsRequest,
  GetEmployeeInfoRequest,
  GetEmployeeIntegrityCheckRequest,
  GetEmployeeMonthlyStaffCostGroupsRequest,
  GetEmployeeProfileRequest,
  ImportEmployeeDataExcelRequest,
  ImportVendorCostReportRequest,
  ListEmployeePublicInfoRequest,
  UploadEmployeeDocumentRequest,
} from '@/features/employee-data/employee-data-request.def.ts'
import { HcError } from '@/common/types/api.def.ts'
import { downloadExcel, downloadFile } from '@/utils/misc.ts'
import { HcFile } from '@/common/types/personal-info.def.ts'
import { EmployeeOrgChartNodeInfo } from '@/features/employee-data/organization-chart.def.ts'
import { MonthlyStaffCostGroupItem } from '@/features/employee-data/staff-cost.def.ts'
import { GetEmployeeDetailsWithTimelineResponse } from '@/features/employee-data/employee-data-response.def.ts'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import { GetEmployeeProfileResponse } from '@/features/employee-data/employee-profile.def.ts'
import { EmployeeEventItem } from '@/features/employee-data/employee-event.def.ts'


export const employeeDataApi = createApi({
  reducerPath: 'employeeDataApi',
  tagTypes: ['EmployeeInfo', 'WorkInfoHistory', 'StaffCost', 'EmployeeActionSummary', 'RewardExercise', 'EmployeeProfile', 'EmployeeEvent'],
  baseQuery: hcPortalBaseQuery(),
  endpoints: builder => ({
    addEmployee: builder.mutation<EmployeeInfo, AddEmployeeRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddEmployee',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: () => {
        return [{ type: 'EmployeeInfo' }]
      },
    }),
    editEmployee: builder.mutation<boolean, EditEmployeeRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditEmployee',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteEmployee: builder.mutation<boolean, DeleteEmployeeRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteEmployee',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    getEmployeeInfo: builder.query<IEmployeeInfo, GetEmployeeInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/GetEmployeeInfo',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    getEmployeeDetailsWithTimeline: builder.query<GetEmployeeDetailsWithTimelineResponse, GetEmployeeDetailsWithTimelineRequest>(
      {
        query: (request) => ({
          url: '/EmployeeData/GetEmployeeDetailsWithTimeline',
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }),
        providesTags: (_result, _error, request) => {
          return [{ type: 'EmployeeInfo', id: request.employeeId }]
        },
      }),
    getEmployeeIntegrityCheck: builder.query<EmployeeIntegrityCheckResult, GetEmployeeIntegrityCheckRequest>({
      query: (request) => ({
        url: '/EmployeeData/GetEmployeeIntegrityCheck',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    listEmployeePublicInfo: builder.query<EmployeePublicInfo[], ListEmployeePublicInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/ListEmployeePublicInfo',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: `LIST_${request.effectiveDate}` }]
      },
    }),
    getEmployeeDirectory: builder.query<EmployeePublicInfo[], GetEmployeeDirectoryRequest>({
      query: (request) => ({
        url: '/EmployeeData/GetEmployeeDirectory',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: `DIRECTORY_${request.effectiveDate}` }]
      },
    }),
    getEmployeeDirectoryAsNonHc: builder.query<EmployeePublicInfo[], void>({
      query: () => ({
        url: '/EmployeeData/GetEmployeeDirectoryAsNonHc',
        method: 'GET',
        cache: 'no-cache',
      }),
      providesTags: () => {
        return [{ type: 'EmployeeInfo', id: `DIRECTORY_AsNonHc` }]
      },
    }),
    addWorkInfo: builder.mutation<boolean, AddWorkInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddWorkInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }, { type: 'WorkInfoHistory', id: request.employeeId }]
      },
    }),
    editWorkInfo: builder.mutation<boolean, EditWorkInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditWorkInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }, { type: 'WorkInfoHistory', id: request.employeeId }]
      },
    }),
    deleteWorkInfo: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteWorkInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }, { type: 'WorkInfoHistory', id: request.employeeId }]
      },
    }),
    addPersonalInfo: builder.mutation<boolean, AddPersonalInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddPersonalInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editPersonalInfo: builder.mutation<boolean, EditPersonalInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditPersonalInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deletePersonalInfo: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeletePersonalInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addEmployment: builder.mutation<boolean, AddEmploymentRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddEmployment',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editEmployment: builder.mutation<boolean, EditEmploymentRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditEmployment',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteEmployment: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteEmployment',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addCostDistribution: builder.mutation<boolean, AddCostDistributionRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddCostDistribution',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editCostDistribution: builder.mutation<boolean, EditCostDistributionRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditCostDistribution',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteCostDistribution: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteCostDistribution',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addFinanceManagedData: builder.mutation<boolean, AddFinanceManagedDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddFinanceManagedData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }, { type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    editFinanceManagedData: builder.mutation<boolean, EditFinanceManagedDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditFinanceManagedData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }, { type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    deleteFinanceManagedData: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteFinanceManagedData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }, { type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    addSalary: builder.mutation<boolean, AddSalaryRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddSalary',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editSalary: builder.mutation<boolean, EditSalaryRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditSalary',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteSalary: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteSalary',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addMedicalInsurance: builder.mutation<boolean, AddMedicalInsuranceRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddMedicalInsurance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editMedicalInsurance: builder.mutation<boolean, EditMedicalInsuranceRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditMedicalInsurance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteMedicalInsurance: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteMedicalInsurance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addLifeInsurance: builder.mutation<boolean, AddLifeInsuranceRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddLifeInsurance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editLifeInsurance: builder.mutation<boolean, EditLifeInsuranceRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditLifeInsurance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteLifeInsurance: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteLifeInsurance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addBankAccount: builder.mutation<boolean, AddBankAccountRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddBankAccount',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editBankAccount: builder.mutation<boolean, EditBankAccountRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditBankAccount',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteBankAccount: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteBankAccount',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addBackgroundCheck: builder.mutation<boolean, AddBackgroundCheckRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddBackgroundCheck',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editBackgroundCheck: builder.mutation<boolean, EditBackgroundCheckRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditBackgroundCheck',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteBackgroundCheck: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteBackgroundCheck',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addAnnualPerformance: builder.mutation<boolean, AddAnnualPerformanceRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddAnnualPerformance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editAnnualPerformance: builder.mutation<boolean, EditAnnualPerformanceRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditAnnualPerformance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteAnnualPerformance: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteAnnualPerformance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addDeferredProgram: builder.mutation<boolean, AddDeferredProgramRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddDeferredProgram',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editDeferredProgram: builder.mutation<boolean, EditDeferredProgramRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditDeferredProgram',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteDeferredProgram: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteDeferredProgram',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addEmploymentHistory: builder.mutation<boolean, AddEmploymentHistoryRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editEmploymentHistory: builder.mutation<boolean, EditEmploymentHistoryRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteEmploymentHistory: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addGroupEmploymentHistory: builder.mutation<boolean, AddGroupEmploymentHistoryRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddGroupEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editGroupEmploymentHistory: builder.mutation<boolean, EditGroupEmploymentHistoryRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditGroupEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteGroupEmploymentHistory: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteGroupEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addEducation: builder.mutation<boolean, AddEducationRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddEducation',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editEducation: builder.mutation<boolean, EditEducationRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditEducation',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteEducation: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteEducation',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addContractEmployment: builder.mutation<boolean, AddContractEmploymentRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddContractEmployment',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editContractEmployment: builder.mutation<boolean, EditContractEmploymentRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditContractEmployment',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteContractEmployment: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteContractEmployment',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addQualification: builder.mutation<boolean, AddQualificationRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddQualification',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editQualification: builder.mutation<boolean, EditQualificationRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditQualification',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteQualification: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteQualification',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addBonus: builder.mutation<boolean, AddBonusRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddBonus',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editBonus: builder.mutation<boolean, EditBonusRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditBonus',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteBonus: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteBonus',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addExceptionalAward: builder.mutation<boolean, AddExceptionalAwardRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddExceptionalAward',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editExceptionalAward: builder.mutation<boolean, EditExceptionalAwardRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditExceptionalAward',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteExceptionalAward: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteExceptionalAward',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addIdentificationDocument: builder.mutation<boolean, AddIdentificationDocumentRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddIdentificationDocument',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editIdentificationDocument: builder.mutation<boolean, EditIdentificationDocumentRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditIdentificationDocument',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteIdentificationDocument: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteIdentificationDocument',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addVisa: builder.mutation<boolean, AddVisaRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddVisa',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editVisa: builder.mutation<boolean, EditVisaRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditVisa',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteVisa: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteVisa',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addDependentInfo: builder.mutation<boolean, AddDependentInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddDependentInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editDependentInfo: builder.mutation<boolean, EditDependentInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditDependentInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteDependentInfo: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteDependentInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addEmergencyContact: builder.mutation<boolean, AddEmergencyContactRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddEmergencyContact',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editEmergencyContact: builder.mutation<boolean, EditEmergencyContactRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditEmergencyContact',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteEmergencyContact: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteEmergencyContact',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addWarningLetter: builder.mutation<boolean, AddWarningLetterRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddWarningLetter',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editWarningLetter: builder.mutation<boolean, EditWarningLetterRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditWarningLetter',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deleteWarningLetter: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeleteWarningLetter',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addPerformanceImprovementPlan: builder.mutation<boolean, AddPerformanceImprovementPlanRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddPerformanceImprovementPlan',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editPerformanceImprovementPlan: builder.mutation<boolean, EditPerformanceImprovementPlanRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditPerformanceImprovementPlan',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deletePerformanceImprovementPlan: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeletePerformanceImprovementPlan',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addPmStaticInfo: builder.mutation<boolean, AddPmStaticInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddPmStaticInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editPmStaticInfo: builder.mutation<boolean, EditPmStaticInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditPmStaticInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deletePmStaticInfo: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeletePmStaticInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addPmInfo: builder.mutation<boolean, AddPmInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddPmInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editPmInfo: builder.mutation<boolean, EditPmInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditPmInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deletePmInfo: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeletePmInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    addPmAnnualPerformance: builder.mutation<boolean, AddPmAnnualPerformanceRequest>({
      query: (request) => ({
        url: '/EmployeeData/AddPmAnnualPerformance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    editPmAnnualPerformance: builder.mutation<boolean, EditPmAnnualPerformanceRequest>({
      query: (request) => ({
        url: '/EmployeeData/EditPmAnnualPerformance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    deletePmAnnualPerformance: builder.mutation<boolean, DeleteEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DeletePmAnnualPerformance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    uploadEmployeeDocument: builder.mutation<HcFile, UploadEmployeeDocumentRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('employeeId', request.employeeId)
        formData.append('document', request.document)
        formData.append('type', request.type)

        return {
          url: '/EmployeeDocument/UploadEmployeeDocument',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
    }),
    downloadEmployeeDocument: builder.mutation<void, DownloadEmployeeDocumentRequest>({
      query: (request) => {
        return {
          url: '/EmployeeDocument/DownloadEmployeeDocument',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(request.fileName, blob)
          },
        }
      },
    }),
    importEmployeeDataExcel: builder.mutation<string[], ImportEmployeeDataExcelRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('excelFile', request.excelFile)

        return {
          url: '/EmployeeData/ImportEmployeeDataExcel',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: (result) => {
        return result?.map(employeeId => ({
          type: 'EmployeeInfo' as const,
          id: employeeId,
        })) ?? []
      },
    }),
    downloadImportTemplate: builder.mutation<void, void>({
      query: () => ({
        url: '/EmployeeData/DownloadImportTemplate',
        method: 'GET',
        cache: 'no-cache',
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`excel_import_template`, blob)
        },
      }),
    }),
    downloadEmployeeData: builder.mutation<void, DownloadEmployeeDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DownloadEmployeeData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`employee_data`, blob)
        },
      }),
    }),
    downloadEmployeeSnapshotData: builder.mutation<void, DownloadEmployeeSnapshotDataRequest>({
      query: (request) => ({
        url: '/EmployeeData/DownloadEmployeeSnapshotData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`employee_snapshot_data_${request.effectiveDate}`, blob)
        },
      }),
    }),
    downloadEmployeeDataChangeLogs: builder.mutation<void, DownloadEmployeeDataChangeLogsRequest>({
      query: (request) => ({
        url: '/EmployeeData/DownloadEmployeeDataChangeLogs',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`employee_data_change_logs_${request.startDate}_${request.endDate}`, blob)
        },
      }),
    }),
    getHistoricalBuyingPower: builder.query<Record<string, BuyingPower[]>, GetEmployeeBuyingPowerRequest>({
      query: (request) => ({
        url: '/EmployeeData/GetHistoricalBuyingPower',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeInfo', id: request.employeeId }]
      },
    }),
    getOrgChartData: builder.query<EmployeeOrgChartNodeInfo[], ListEmployeePublicInfoRequest>({
      query: (request) => ({
        url: '/EmployeeData/GetOrgChartData',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
    }),
    getEmployeeMonthlyStaffCostGroups: builder.query<MonthlyStaffCostGroupItem[], GetEmployeeMonthlyStaffCostGroupsRequest>(
      {
        query: (request) => ({
          url: '/StaffCost/GetEmployeeMonthlyStaffCostGroups',
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }),
        providesTags: (_result, _error, arg) => {
          return [{ type: 'StaffCost', id: `${arg.employeeId}_${arg.year}` }]
        },
      }),
    addStaffCost: builder.mutation<boolean, AddStaffCostRequest>({
      query: (request) => ({
        url: '/StaffCost/AddStaffCost',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => {
        return [{ type: 'StaffCost', id: `${arg.employeeId}_${arg.year}` }]
      },
    }),
    editStaffCost: builder.mutation<boolean, EditStaffCostRequest>({
      query: (request) => ({
        url: '/StaffCost/EditStaffCost',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => {
        return [{ type: 'StaffCost', id: `${arg.employeeId}_${arg.year}` }]
      },
    }),
    bulkUpdateStaffCost: builder.mutation<boolean, BulkUpdateStaffCostRequest>({
      query: (request) => ({
        url: '/StaffCost/BulkUpdateStaffCost',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => {
        return [{ type: 'StaffCost', id: `${arg.employeeId}_${arg.year}` }]
      },
    }),
    deleteStaffCost: builder.mutation<boolean, { id: number, year: number, employeeId: string }>({
      query: (request) => ({
        url: '/StaffCost/DeleteStaffCost',
        method: 'POST',
        cache: 'no-cache',
        body: {
          id: request.id,
          employeeId: request.employeeId,
        } as DeleteEmployeeDataRequest,
      }),
      invalidatesTags: (_result, _error, arg) => {
        return [{ type: 'StaffCost', id: `${arg.employeeId}_${arg.year}` }]
      },
    }),
    bulkDeleteStaffCost: builder.mutation<boolean, BulkDeleteStaffCostRequest>({
      query: (request) => ({
        url: '/StaffCost/BulkDeleteStaffCost',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => {
        return [{ type: 'StaffCost', id: `${arg.employeeId}_${arg.year}` }]
      },
    }),
    bulkImportStaffCost: builder.mutation<void, BulkImportStaffCostRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('excelFile', request.excelFile)

        return {
          url: '/StaffCost/BulkImportStaffCost',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
    }),
    importVendorCostReport: builder.mutation<void, ImportVendorCostReportRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('type', request.type)
        formData.append('country', request.country)
        formData.append('sheetName', request.sheetName)
        formData.append('excelFile', request.excelFile)

        return {
          url: '/VendorCostReport/ImportVendorCostReport',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadExcel(`staff_cost_from_${request.type}_${request.country}`, blob)
          },
        }
      },
    }),
    exportStaffCostImportTemplate: builder.mutation<void, void>({
      query: () => ({
        url: '/StaffCost/ExportStaffCostImportTemplate',
        method: 'GET',
        cache: 'no-cache',
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel('staff_cost_import_template', blob)
        },
      }),
    }),
    exportStaffCostData: builder.mutation<void, ExportStaffCostDataRequest>({
      query: (request) => ({
        url: '/StaffCost/ExportStaffCostData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          const filename = request.month ?
            `staff_cost_${request.year}_${request.month}` :
            `staff_cost_${request.year}`
          downloadExcel(filename, blob)
        },
      }),
    }),
    downloadFundStaffCostReport: builder.mutation<void, DownloadFundStaffCostReportRequest>({
      query: (request) => ({
        url: '/StaffCost/DownloadFundStaffCostReport',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`fund_staff_cost_${request.targetDate.substring(0, 7)}`, blob)
        },
      }),
    }),
    downloadSalaryHistoryReport: builder.mutation<void, DownloadSalaryHistoryReportRequest>({
      query: (request) => ({
        url: '/StaffCost/DownloadSalaryHistoryReport',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`salary_history_${request.targetDate.substring(0, 7)}`, blob)
        },
      }),
    }),
    downloadHeadcountPassthroughReport: builder.mutation<void, DownloadHeadcountPassthroughReportRequest>({
      query: (request) => ({
        url: '/StaffCost/DownloadHeadcountPassthroughReport',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`headcount_passthrough_${request.targetDate}`, blob)
        },
      }),
    }),
    getEmployeeCeremonyData: builder.query<EmployeeCeremonyResponse, void>({
      query: () => ({
        url: '/EmployeeData/GetEmployeeCeremonyData',
        method: 'GET',
        cache: 'no-cache',
      }),
    }),
    getEmployeeProfile: builder.query<GetEmployeeProfileResponse, GetEmployeeProfileRequest>(
      {
        query: (request) => ({
          url: '/EmployeeProfile/GetEmployeeProfile',
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }),
        providesTags: (_result, _error, request) => {
          return [{ type: 'EmployeeProfile', id: request.employeeId }]
        },
      }),
    getEmployeeEvents: builder.query<EmployeeEventItem[], GetEmployeeEventsRequest>(
      {
        query: (request) => ({
          url: '/EmployeeProfile/GetEmployeeEvents',
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }),
        providesTags: (_result, _error, request) => {
          return [{ type: 'EmployeeEvent', id: request.employeeId }]
        },
      }),
    triggerSelfServiceChangeNotification: builder.mutation<void, void>({
      query: () => ({
        url: '/EmployeeProfile/TriggerSelfServiceChangeNotification',
        method: 'POST',
        cache: 'no-cache',
      }),
    }),
    selfAddPersonalInfo: builder.mutation<boolean, AddPersonalInfoRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddPersonalInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddDependentInfo: builder.mutation<boolean, AddDependentInfoRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddDependentInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditDependentInfo: builder.mutation<boolean, EditDependentInfoRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditDependentInfo',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddEmergencyContact: builder.mutation<boolean, AddEmergencyContactRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddEmergencyContact',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditEmergencyContact: builder.mutation<boolean, EditEmergencyContactRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditEmergencyContact',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddIdentificationDocument: builder.mutation<boolean, AddIdentificationDocumentRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddIdentificationDocument',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditIdentificationDocument: builder.mutation<boolean, EditIdentificationDocumentRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditIdentificationDocument',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddVisa: builder.mutation<boolean, AddVisaRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddVisa',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditVisa: builder.mutation<boolean, EditVisaRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditVisa',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddBankAccount: builder.mutation<boolean, AddBankAccountRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddBankAccount',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditBankAccount: builder.mutation<boolean, EditBankAccountRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditBankAccount',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddEducation: builder.mutation<boolean, AddEducationRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddEducation',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditEducation: builder.mutation<boolean, EditEducationRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditEducation',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddQualification: builder.mutation<boolean, AddQualificationRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddQualification',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditQualification: builder.mutation<boolean, EditQualificationRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditQualification',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfAddEmploymentHistory: builder.mutation<boolean, AddEmploymentHistoryRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/AddEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
    selfEditEmploymentHistory: builder.mutation<boolean, EditEmploymentHistoryRequest>({
      query: (request) => ({
        url: '/EmployeeProfile/EditEmploymentHistory',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'EmployeeProfile', id: request.employeeId }]
      },
    }),
  }),
})

export const {
  useAddEmployeeMutation,
  useEditEmployeeMutation,
  useDeleteEmployeeMutation,
  useGetEmployeeInfoQuery,
  useGetEmployeeDetailsWithTimelineQuery,
  useGetEmployeeIntegrityCheckQuery,
  useListEmployeePublicInfoQuery,
  useGetEmployeeDirectoryQuery,
  useGetEmployeeDirectoryAsNonHcQuery,
  useAddWorkInfoMutation,
  useEditWorkInfoMutation,
  useDeleteWorkInfoMutation,
  useAddPersonalInfoMutation,
  useEditPersonalInfoMutation,
  useDeletePersonalInfoMutation,
  useAddEmploymentMutation,
  useEditEmploymentMutation,
  useDeleteEmploymentMutation,
  useAddSalaryMutation,
  useEditSalaryMutation,
  useDeleteSalaryMutation,
  useAddBankAccountMutation,
  useEditBankAccountMutation,
  useDeleteBankAccountMutation,
  useAddBackgroundCheckMutation,
  useEditBackgroundCheckMutation,
  useDeleteBackgroundCheckMutation,
  useAddDeferredProgramMutation,
  useEditDeferredProgramMutation,
  useDeleteDeferredProgramMutation,
  useAddEmploymentHistoryMutation,
  useEditEmploymentHistoryMutation,
  useDeleteEmploymentHistoryMutation,
  useAddGroupEmploymentHistoryMutation,
  useEditGroupEmploymentHistoryMutation,
  useDeleteGroupEmploymentHistoryMutation,
  useAddEducationMutation,
  useEditEducationMutation,
  useDeleteEducationMutation,
  useAddQualificationMutation,
  useEditQualificationMutation,
  useDeleteQualificationMutation,
  useAddBonusMutation,
  useEditBonusMutation,
  useDeleteBonusMutation,
  useAddExceptionalAwardMutation,
  useEditExceptionalAwardMutation,
  useDeleteExceptionalAwardMutation,
  useAddIdentificationDocumentMutation,
  useEditIdentificationDocumentMutation,
  useDeleteIdentificationDocumentMutation,
  useAddVisaMutation,
  useEditVisaMutation,
  useDeleteVisaMutation,
  useAddDependentInfoMutation,
  useEditDependentInfoMutation,
  useDeleteDependentInfoMutation,
  useAddEmergencyContactMutation,
  useEditEmergencyContactMutation,
  useDeleteEmergencyContactMutation,
  useAddWarningLetterMutation,
  useEditWarningLetterMutation,
  useDeleteWarningLetterMutation,
  useAddPerformanceImprovementPlanMutation,
  useEditPerformanceImprovementPlanMutation,
  useDeletePerformanceImprovementPlanMutation,
  useAddPmStaticInfoMutation,
  useEditPmStaticInfoMutation,
  useDeletePmStaticInfoMutation,
  useAddPmAnnualPerformanceMutation,
  useEditPmAnnualPerformanceMutation,
  useDeletePmAnnualPerformanceMutation,
  useAddPmInfoMutation,
  useEditPmInfoMutation,
  useDeletePmInfoMutation,
  useUploadEmployeeDocumentMutation,
  useDownloadEmployeeDocumentMutation,
  useImportEmployeeDataExcelMutation,
  useDownloadEmployeeDataMutation,
  useDownloadEmployeeSnapshotDataMutation,
  useDownloadEmployeeDataChangeLogsMutation,
  useDownloadImportTemplateMutation,
  useImportVendorCostReportMutation,
  useGetHistoricalBuyingPowerQuery,
  useGetOrgChartDataQuery,
  useGetEmployeeMonthlyStaffCostGroupsQuery,
  useAddStaffCostMutation,
  useEditStaffCostMutation,
  useBulkUpdateStaffCostMutation,
  useBulkDeleteStaffCostMutation,
  useDeleteStaffCostMutation,
  useBulkImportStaffCostMutation,
  useExportStaffCostImportTemplateMutation,
  useExportStaffCostDataMutation,
  useDownloadFundStaffCostReportMutation,
  useDownloadSalaryHistoryReportMutation,
  useDownloadHeadcountPassthroughReportMutation,
  useAddCostDistributionMutation,
  useEditCostDistributionMutation,
  useDeleteCostDistributionMutation,
  useAddFinanceManagedDataMutation,
  useEditFinanceManagedDataMutation,
  useDeleteFinanceManagedDataMutation,
  useAddMedicalInsuranceMutation,
  useEditMedicalInsuranceMutation,
  useDeleteMedicalInsuranceMutation,
  useAddLifeInsuranceMutation,
  useEditLifeInsuranceMutation,
  useDeleteLifeInsuranceMutation,
  useAddContractEmploymentMutation,
  useEditContractEmploymentMutation,
  useDeleteContractEmploymentMutation,
  useAddAnnualPerformanceMutation,
  useEditAnnualPerformanceMutation,
  useDeleteAnnualPerformanceMutation,
  useGetEmployeeProfileQuery,
  useGetEmployeeEventsQuery,
  useTriggerSelfServiceChangeNotificationMutation,
  useSelfAddPersonalInfoMutation,
  useSelfAddDependentInfoMutation,
  useSelfEditDependentInfoMutation,
  useSelfAddEmergencyContactMutation,
  useSelfEditEmergencyContactMutation,
  useSelfAddIdentificationDocumentMutation,
  useSelfEditIdentificationDocumentMutation,
  useSelfAddVisaMutation,
  useSelfEditVisaMutation,
  useSelfAddBankAccountMutation,
  useSelfEditBankAccountMutation,
  useSelfAddEducationMutation,
  useSelfEditEducationMutation,
  useSelfAddQualificationMutation,
  useSelfEditQualificationMutation,
  useSelfAddEmploymentHistoryMutation,
  useSelfEditEmploymentHistoryMutation,
  useGetEmployeeCeremonyDataQuery,
} = employeeDataApi